import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { weeklyText } from "../../lib/weeklyRichText.js";
import { T, PROJECT_STATUS, PROJECT_TYPE } from "../../app/tokens.js";
import { Card } from "../../components/Card.jsx";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import { MilestoneSignal, BudgetHealthLabel, budgetHealthColor } from "./ProjectDisplaySignals.jsx";
import { selectNextMilestone, budgetHealth } from "../../lib/projectDisplaySignals.js";
import {
  createDecision,
  createProjectDocument,
  createMilestone,
  createRisk,
  createWeeklyUpdate,
  getProject,
  getProjectDocuments,
  getProjectSlackSource,
  getProjectMilestones,
  getProjectRisks,
  updateDecision,
  updateProjectDocument,
  updateMilestone,
  updateRisk,
  deleteProjectDocument,
  createProjectSource,
  deleteProjectSource,
  getProjectIntelProposals,
  extractProjectIntel,
  editProjectIntelProposal,
  approveProjectIntelProposal,
  dismissProjectIntelProposal,
} from "../../data/projectsClient.js";
import { fmtDate } from "../../utils/format.js";
import { log } from "../../utils/logger.js";
import { ProjectEditModal } from "./ProjectEditPanel.jsx";
import {
  projectStatusToHealthKey,
  sortRisksBySeverity,
  getProjectPhaseSteps,
  getProjectPhaseDisplayLabel,
} from "./projectFilters.js";
import { formatRecordAmount, recordIdentity, recordVariance } from "./projectRecord.js";
import { selectNextPortfolioMilestone } from "./portfolioRowModel.js";
import { ProjectHealthRing } from "./ProjectHealthRing.jsx";
import { ProjectWorkingUpdates } from "./weekly/ProjectWorkingUpdates.jsx";
import { useSensitivityPolicy } from "../../contexts/SensitivityPolicyContext.jsx";
import { useFeatureFlags } from "../../config/featureFlags.js";
import { getProjectSourceDisplayName, ProjectSourceChips, ProjectSourcePaste } from "./ProjectSourceLinks.jsx";

const WRITE_ROLES = new Set(["admin", "editor"]);
export const STATUS_LABELS = { open: "Open", decided: "Decided", deferred: "Deferred" };
export const RISK_STATUS_LABELS = { open: "Open", mitigated: "Mitigated", accepted: "Accepted", closed: "Closed" };
export const MILESTONE_STATUS_LABELS = { on_track: "On Track", at_risk: "At Risk", complete: "Complete" };

export function isSafeDocumentUrl(value) {
  if (typeof value !== "string" || !value.trim()) return false;
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

export function ProjectPhaseTracker({ phase }) {
  const steps = getProjectPhaseSteps(phase);
  return (
    <section
      aria-label="Schedule phase"
      data-testid="project-phase-tracker"
      style={{
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        padding: "12px 14px 14px",
        marginBottom: 12,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, color: T.mid, fontSize: T.fsXs, fontWeight: T.fwBold, letterSpacing: ".04em", textTransform: "uppercase", marginBottom: 10 }}>
        <span>Schedule phase</span>
        {steps.every((step) => step.state === "done") && (
          <span data-testid="project-phase-terminal" style={{ color: T.green, background: T.greenSoft, borderRadius: 5, padding: "2px 6px", letterSpacing: 0 }}>
            Complete
          </span>
        )}
      </div>
      <div style={{ overflowX: "auto", padding: "4px 0" }}>
      <div style={{ display: "grid", minWidth: 560, gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`, alignItems: "start" }}>
        {steps.map((step, index) => {
          const done = step.state === "done";
          const current = step.state === "current";
          const active = done || current;
          return (
            <div
              key={step.key}
              data-testid={`project-phase-step-${step.key}`}
              data-state={step.state}
              aria-current={current ? "step" : undefined}
              style={{ position: "relative", minWidth: 0, textAlign: "center" }}
            >
              {index > 0 && (
                <span
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    top: 6,
                    right: "50%",
                    width: "100%",
                    height: 2,
                    background: active ? T.projectsHubNavy : T.borderDark,
                  }}
                />
              )}
              <span
                aria-hidden="true"
                style={{
                  position: "relative",
                  zIndex: 1,
                  display: "inline-flex",
                  width: 14,
                  height: 14,
                  borderRadius: "50%",
                  alignItems: "center",
                  justifyContent: "center",
                  color: T.white,
                  background: done ? T.projectsHubNavy : current ? T.blue : T.borderDark,
                  border: `2px solid ${done ? T.projectsHubNavy : current ? T.blue : T.borderDark}`,
                  boxShadow: current ? `0 0 0 4px ${T.blueSoft}` : "none",
                  boxSizing: "border-box",
                }}
              />
              <div
                style={{
                  marginTop: 5,
                  padding: "0 3px",
                  color: active ? T.projectsHubNavy : T.mid,
                  fontSize: T.fsXxxs5,
                  fontWeight: current ? T.fwBold : T.fwSemibold,
                  lineHeight: 1.2,
                }}
              >
                {step.label}
              </div>
            </div>
          );
        })}
      </div>
      </div>
    </section>
  );
}

export const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  padding: "6px 8px",
  fontSize: T.fsSm,
  color: T.dark,
  background: T.white,
};
export const labelStyle = {
  display: "block",
  color: T.mid,
  fontSize: T.fsXs,
  fontWeight: T.fwSemibold,
  marginBottom: 3,
};

function Pill({ children, color = T.mid, background = T.lightGray }) {
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", borderRadius: 6,
      padding: "2px 8px", fontSize: T.fsXs, fontWeight: T.fwSemibold,
      color, background, whiteSpace: "nowrap",
    }}>
      {children}
    </span>
  );
}

function StatusPill({ status }) {
  const value = PROJECT_STATUS[status];
  return value ? <Pill color={value.color} background={value.bg}>{value.label}</Pill> : <span style={{ color: T.light }}>—</span>;
}

function Section({ title, children, action, embedded = false, record = false }) {
  const Surface = embedded ? "section" : Card;
  return (
    <Surface style={{ padding: embedded ? "14px 0 0" : "14px 16px", ...(record ? { borderTop: `3px solid ${T.projectsHubNavy}` } : {}) }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: 10, borderBottom: `1px solid ${T.border}`, paddingBottom: 8,
      }}>
        <h3 style={{
          margin: 0, color: record ? T.projectsHubNavy : T.mid, fontSize: T.fsXs, fontWeight: T.fwBold,
          letterSpacing: 0.5, textTransform: "uppercase",
        }}>{title}</h3>
        {action}
      </div>
      {children}
    </Surface>
  );
}

function Empty({ children }) {
  return <div style={{ padding: "14px 0", color: T.light, fontSize: T.fsSm, fontStyle: "italic" }}>{children}</div>;
}

export function ActionButton({ children, onClick, secondary = false, disabled = false, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      disabled={disabled}
      onClick={onClick}
      style={{
        border: `1px solid ${secondary ? T.border : T.blue}`,
        borderRadius: 6,
        background: secondary ? T.white : T.blue,
        color: secondary ? T.mid : T.white,
        padding: "5px 9px",
        fontSize: T.fsXs,
        fontWeight: T.fwSemibold,
        cursor: disabled ? "wait" : "pointer",
        opacity: disabled ? 0.65 : 1,
      }}
    >
      {children}
    </button>
  );
}

export function Field({ label, value, onChange, type = "text", placeholder }) {
  return (
    <label style={{ display: "block", marginBottom: 8 }}>
      <span style={labelStyle}>{label}</span>
      <input type={type} value={value ?? ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} style={inputStyle} />
    </label>
  );
}

export function SelectField({ label, value, onChange, options }) {
  return (
    <label style={{ display: "block", marginBottom: 8 }}>
      <span style={labelStyle}>{label}</span>
      <select value={value ?? ""} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
        {options.map(([key, text]) => <option key={key} value={key}>{text}</option>)}
      </select>
    </label>
  );
}

export function DocumentForm({ value, onChange, onSave, onCancel, saving }) {
  return (
    <div data-testid="project-document-form" style={{ background: T.blueSoft, borderRadius: 8, padding: 10, marginTop: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <SelectField
          label="Source"
          value={value.source}
          onChange={(v) => onChange("source", v)}
          options={[["JLL Weekly", "JLL Weekly"], ["PMO Budget", "PMO Budget"], ["Other", "Other"]]}
        />
        <Field label="Page reference (optional)" value={value.pageRef} onChange={(v) => onChange("pageRef", v)} placeholder="e.g. p. 4" />
      </div>
      <Field label="URL" type="url" value={value.url} onChange={(v) => onChange("url", v)} placeholder="https://…" />
      <div style={{ display: "flex", gap: 6 }}>
        <ActionButton onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save"}</ActionButton>
        <ActionButton onClick={onCancel} secondary disabled={saving}>Cancel</ActionButton>
      </div>
    </div>
  );
}

const SOURCE_ICONS = {
  slack: ["#", T.purple],
  google: ["G", T.blue],
  atlassian: ["J", T.decisionFg],
  asana: ["A", T.pendingAmber],
  other: ["↗", T.mid],
};

export function Connections({ projectId, project, documents, slackSource, canEdit, onChange, onProjectUpdated, sourceData = null, onSourcesChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const popoverId = `project-sources-popover-${projectId}`;
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mutationError, setMutationError] = useState(null);
  const [typedSources, setTypedSources] = useState(() => Array.isArray(sourceData) ? sourceData : []);

  useEffect(() => {
    if (Array.isArray(sourceData)) setTypedSources(sourceData);
  }, [sourceData]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      if (!containerRef.current?.contains(event.target) && !popoverRef.current?.contains(event.target)) setIsOpen(false);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    const position = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setPopoverPosition({
        top: Math.min(rect.bottom + 8, Math.max(12, window.innerHeight - 120)),
        left: Math.max(12, Math.min(rect.right - 340, window.innerWidth - 352)),
      });
    };
    position();
    popoverRef.current?.focus();
    window.addEventListener("resize", position);
    window.addEventListener("scroll", position, true);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", position);
      window.removeEventListener("scroll", position, true);
    };
  }, [isOpen]);

  const sourceTypes = [...new Set(typedSources.map((source) => source?.sourceType || "other"))];

  const start = (item = null) => {
    setEditing(item?.id ?? "new");
    setDraft(item
      ? { source: item.source || "Other", url: item.url || "", pageRef: item.pageRef || "" }
      : { source: "JLL Weekly", url: "", pageRef: "" });
  };

  const save = async () => {
    if (!draft?.source || !draft.url?.trim()) {
      setMutationError(new Error("Source and URL are required."));
      return;
    }
    setSaving(true);
    setMutationError(null);
    try {
      const payload = { ...draft, url: draft.url.trim(), pageRef: draft.pageRef?.trim() || null };
      const result = editing === "new"
        ? await createProjectDocument(projectId, payload)
        : await updateProjectDocument(projectId, editing, payload);
      onChange((prev) => editing === "new" ? [...prev, result] : prev.map((item) => item.id === result.id ? result : item));
      if (editing === "new") onProjectUpdated?.({ ...project, hasDocuments: true });
      setEditing(null);
      setDraft(null);
    } catch (err) {
      log.warn("ProjectInlineExpanded: document save failed", { projectId, err: String(err) });
      setMutationError(err);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (item) => {
    setSaving(true);
    setMutationError(null);
    try {
      await deleteProjectDocument(projectId, item.id);
      onChange((prev) => prev.filter((document) => document.id !== item.id));
      if (documents.length === 1) onProjectUpdated?.({ ...project, hasDocuments: false });
    } catch (err) {
      log.warn("ProjectInlineExpanded: document delete failed", { projectId, err: String(err) });
      setMutationError(err);
    } finally {
      setSaving(false);
    }
  };

  const addTypedSource = async (payload) => {
    if (typeof createProjectSource !== "function") throw new Error("Source links are unavailable.");
    const created = await createProjectSource(projectId, payload);
    setTypedSources((current) => [...current, created]);
    onSourcesChange?.((current) => [...current, created]);
  };

  const removeTypedSource = async (item) => {
    try {
      if (typeof deleteProjectSource !== "function") throw new Error("Source links are unavailable.");
      await deleteProjectSource(item.id, projectId);
      setTypedSources((current) => current.filter((source) => source.id !== item.id));
      onSourcesChange?.((current) => current.filter((source) => source.id !== item.id));
      setMutationError(null);
    } catch (err) {
      setMutationError(err);
    }
  };

  return (
    <div ref={containerRef} data-testid="project-sources-control" style={{ position: "relative", flexShrink: 0 }}>
      <button ref={triggerRef} type="button" data-testid="project-sources-trigger"
        aria-expanded={isOpen} aria-controls={popoverId} aria-haspopup="dialog"
        onClick={() => setIsOpen((open) => !open)}
        style={{ display: "inline-flex", alignItems: "center", gap: 6, border: `1px solid ${T.border}`, borderRadius: 8, background: T.white, color: T.dark, padding: "6px 9px", fontSize: T.fsSm, fontWeight: T.fwSemibold, cursor: "pointer" }}>
        <span aria-hidden="true" style={{ display: "inline-flex", alignItems: "center" }}>
          {sourceTypes.map((type, index) => {
            const [icon, color] = SOURCE_ICONS[type] || SOURCE_ICONS.other;
            return <span key={type} data-testid={`project-source-icon-${type}`} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 17, height: 17, marginLeft: index ? -5 : 0, borderRadius: 4, border: `1px solid ${T.white}`, background: color, color: T.white, fontSize: T.fsXxxs, fontWeight: T.fwBold, zIndex: sourceTypes.length - index }}>{icon}</span>;
          })}
        </span>
        Sources <span data-testid="project-sources-count">({typedSources.length})</span><span aria-hidden="true">▾</span>
      </button>
      {isOpen && createPortal(<div ref={popoverRef} id={popoverId} role="dialog" tabIndex={-1} aria-label={`Sources for ${project.name}`} data-testid="project-sources-popover"
        style={{ position: "fixed", top: popoverPosition.top, left: popoverPosition.left, width: 340, boxSizing: "border-box", maxWidth: "calc(100vw - 24px)", maxHeight: "min(70vh, 550px)", overflowY: "auto", padding: "12px 14px", border: `1px solid ${T.border}`, borderRadius: 10, background: T.white, boxShadow: "0 8px 24px rgba(16,36,58,0.16)", zIndex: 1000 }}>
      <div style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwBold, marginBottom: 8 }}>Project sources</div>
      {mutationError && (
        <ErrorBanner
          error={mutationError}
          onDismiss={() => setMutationError(null)}
        />
      )}
        <div data-testid="project-typed-sources" style={{ padding: "7px 0", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwSemibold, marginBottom: 7 }}>Project and portfolio sources</div>
          <ProjectSourceChips sources={typedSources} canEdit={canEdit} onRemove={removeTypedSource} showScope />
          <ProjectSourcePaste canEdit={canEdit} onAdd={addTypedSource} label="Paste a project source link" testId="project-source-paste" />
        </div>
      <div data-testid="project-slack-connection" style={{ padding: "7px 0", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwSemibold }}>Slack channel</div>
        <div style={{ color: T.dark, fontSize: T.fsSm, marginTop: 3 }}>
          {slackSource?.reference || project.slackChannel
            ? getProjectSourceDisplayName({
              sourceType: "slack",
              rawUrl: slackSource?.reference,
              label: slackSource?.channelName || project.slackChannel,
            })
            : "No Slack channel configured"}
        </div>
        <div
          data-testid="project-slack-status"
          style={{
            color: slackSource?.status === "connected" ? T.green : T.mid,
            fontSize: T.fsXxs,
            fontWeight: T.fwSemibold,
            marginTop: 5,
          }}
        >
          {slackSource?.status === "connected"
            ? "Connected · read-only"
            : slackSource?.status === "degraded"
              ? `Degraded · ${String(slackSource.reason || "unavailable").replaceAll("_", " ")}`
              : slackSource === null
                ? "Checking connector status…"
                : "Pending connector configuration"}
        </div>
        {slackSource?.readable && (
          <div data-testid="project-slack-messages" style={{ marginTop: 7 }}>
            <div style={{ color: T.mid, fontSize: T.fsXxs }}>
              {slackSource.messageCount || 0} new message{slackSource.messageCount === 1 ? "" : "s"} since last sync
            </div>
            {(slackSource.messages || []).slice(0, 5).map((message) => (
              <div key={message.ts} style={{ color: T.dark, fontSize: T.fsXs, marginTop: 5, whiteSpace: "pre-wrap" }}>
                {message.text || "(non-text Slack message)"}
              </div>
            ))}
          </div>
        )}
      </div>
      <div data-testid="project-drive-connection" style={{ padding: "7px 0", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwSemibold }}>Google Drive</div>
        <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 3 }}>Pending connector setup</div>
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, color: T.mid, fontSize: T.fsXs, fontWeight: T.fwSemibold }}>
        <span>Documents</span>
        {canEdit && <ActionButton onClick={() => start()} testId="project-add-document">+ Add document</ActionButton>}
      </div>
      {documents.length === 0 && !project.documentLink && !draft ? <Empty>No source documents linked.</Empty> : documents.map((item) => {
        const documentLabel = getProjectSourceDisplayName({ sourceType: "other", label: item.source });
        return (
          <div key={item.id} data-testid={`project-document-${item.id}`} style={{ padding: "9px 0", borderBottom: `1px solid ${T.border}` }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
              <strong style={{ color: T.dark, fontSize: T.fsSm }}>{documentLabel}</strong>
              {canEdit && (
                <span style={{ display: "inline-flex", gap: 8 }}>
                  <button type="button" data-testid={`project-document-edit-${item.id}`} onClick={() => start(item)} style={{ border: 0, background: "none", color: T.blue, padding: 0, fontSize: T.fsXs, cursor: "pointer" }}>Edit</button>
                  <button type="button" data-testid={`project-document-remove-${item.id}`} onClick={() => remove(item)} disabled={saving} style={{ border: 0, background: "none", color: T.red, padding: 0, fontSize: T.fsXs, cursor: "pointer" }}>Remove</button>
                </span>
              )}
            </div>
            <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 3 }}>
              {item.pageRef ? `Page ${item.pageRef} · ` : ""}
              {isSafeDocumentUrl(item.url)
                ? <a href={item.url.trim()} target="_blank" rel="noopener noreferrer" aria-label={`Open ${documentLabel} document`} style={{ color: T.blue }}>Open document</a>
                : <span data-testid={`project-document-unsafe-${item.id}`}>Link unavailable</span>}
            </div>
            {editing === item.id && <DocumentForm value={draft} onChange={(key, value) => setDraft((prev) => ({ ...prev, [key]: value }))} onSave={save} onCancel={() => { setEditing(null); setDraft(null); }} saving={saving} />}
          </div>
        );
      })}
      {project.documentLink && !documents.some((item) => item.url?.trim() === project.documentLink.trim()) && (
        <div data-testid="project-legacy-document-link" style={{ padding: "9px 0", borderBottom: `1px solid ${T.border}` }}>
          <strong style={{ color: T.dark, fontSize: T.fsSm }}>Legacy document link</strong>
          <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 3 }}>
            {isSafeDocumentUrl(project.documentLink)
              ? <a href={project.documentLink.trim()} target="_blank" rel="noopener noreferrer" style={{ color: T.blue }}>Open document</a>
              : <span>Link unavailable</span>}
          </div>
        </div>
      )}
      {editing === "new" && draft && <DocumentForm value={draft} onChange={(key, value) => setDraft((prev) => ({ ...prev, [key]: value }))} onSave={save} onCancel={() => { setEditing(null); setDraft(null); }} saving={saving} />}
      </div>, document.body)}
    </div>
  );
}

export function ChildForm({ kind, value, onChange, onSave, onCancel, saving }) {
  const isDecision = kind === "decision";
  const isRisk = kind === "risk";
  return (
    <div data-testid={`project-${kind}-form`} style={{ background: T.blueSoft, borderRadius: 8, padding: 10, marginTop: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 }}>
        <Field label="Title" value={value.title} onChange={(v) => onChange("title", v)} />
        {isDecision && <Field label="Needed by" type="date" value={value.neededByDate} onChange={(v) => onChange("neededByDate", v)} />}
        {isRisk && <SelectField label="Severity" value={value.severity} onChange={(v) => onChange("severity", v)} options={[["high", "High"], ["med", "Medium"], ["low", "Low"]]} />}
        <Field label={isRisk ? "Mitigation" : "Owner"} value={isRisk ? value.mitigation : value.owner} onChange={(v) => onChange(isRisk ? "mitigation" : "owner", v)} />
        {isRisk && <Field label="Owner" value={value.owner} onChange={(v) => onChange("owner", v)} />}
        {isDecision && <SelectField label="Status" value={value.status} onChange={(v) => onChange("status", v)} options={Object.entries(STATUS_LABELS)} />}
        {isRisk && <SelectField label="Status" value={value.status} onChange={(v) => onChange("status", v)} options={Object.entries(RISK_STATUS_LABELS)} />}
        {isDecision && <Field label="Impact if delayed" value={value.impactIfDelayed} onChange={(v) => onChange("impactIfDelayed", v)} />}
      </div>
      <label style={{ display: "block", marginBottom: 8 }}>
        <span style={labelStyle}>Description</span>
        <textarea value={value.description ?? ""} onChange={(e) => onChange("description", e.target.value)} style={{ ...inputStyle, minHeight: 52, resize: "vertical" }} />
      </label>
      <div style={{ display: "flex", gap: 6 }}>
        <ActionButton onClick={onSave} disabled={saving}>{saving ? "Saving…" : "Save"}</ActionButton>
        <ActionButton onClick={onCancel} secondary disabled={saving}>Cancel</ActionButton>
      </div>
    </div>
  );
}

function DecisionList({ projectId, decisions, canEdit, onChange }) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const start = (item) => {
    setSaveError(null);
    setEditing(item?.id ?? "new");
    setAdding(!item);
    setDraft(item ? { ...item } : { title: "", description: "", owner: "", neededByDate: "", impactIfDelayed: "", status: "open" });
  };
  const save = async () => {
    if (!draft?.title?.trim()) return;
    setSaving(true); setSaveError(null);
    try {
      const payload = { ...draft, title: draft.title.trim() };
      const result = adding ? await createDecision(projectId, payload) : await updateDecision(projectId, editing, payload);
      onChange((prev) => adding ? [...prev, result] : prev.map((item) => item.id === result.id ? result : item));
      setEditing(null); setDraft(null);
    } catch (err) { setSaveError(err?.message || "Save failed."); log.warn("ProjectInlineExpanded: decision save failed", { err: String(err) }); }
    finally { setSaving(false); }
  };
  return (
    <Section title={`Decisions (${decisions.length})`} action={canEdit && <ActionButton onClick={() => start(null)} testId="project-add-decision">+ Add</ActionButton>}>
      {decisions.length === 0 && !draft ? <Empty>No decisions recorded.</Empty> : decisions.map((item) => (
        <div key={item.id} data-testid={`project-decision-${item.id}`} style={{ borderBottom: `1px solid ${T.border}`, padding: "7px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <strong style={{ fontSize: T.fsSm, color: T.dark }}>{item.title}</strong>
            <Pill color={item.status === "open" ? T.amberDark : T.mid} background={item.status === "open" ? T.utilAmber : T.lightGray}>{STATUS_LABELS[item.status] || item.status}</Pill>
          </div>
          <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 3 }}>
            {item.owner || "No owner"}{item.neededByDate ? ` · Needed ${fmtDate(item.neededByDate)}` : ""}
          </div>
          {item.description && <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 3 }}>{item.description}</div>}
          {canEdit && <button type="button" onClick={() => start(item)} style={{ border: 0, background: "none", color: T.blue, padding: "5px 0 0", fontSize: T.fsXs, cursor: "pointer" }}>Edit</button>}
          {editing === item.id && <ChildForm kind="decision" value={draft} onChange={(key, value) => setDraft((prev) => ({ ...prev, [key]: value }))} onSave={save} onCancel={() => { setEditing(null); setDraft(null); }} saving={saving} />}
        </div>
      ))}
      {adding && draft && <ChildForm kind="decision" value={draft} onChange={(key, value) => setDraft((prev) => ({ ...prev, [key]: value }))} onSave={save} onCancel={() => { setEditing(null); setDraft(null); }} saving={saving} />}
    {saveError && <div role="alert" data-testid="project-child-save-error" style={{ color: T.red, fontSize: T.fsXs, marginTop: 6 }}>{saveError}</div>}
    </Section>
  );
}

function RiskList({ projectId, risks, canEdit, onChange }) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const start = (item) => {
    setSaveError(null);
    setEditing(item?.id ?? "new"); setAdding(!item);
    setDraft(item ? { ...item } : { title: "", description: "", severity: "med", mitigation: "", owner: "", status: "open" });
  };
  const save = async () => {
    if (!draft?.title?.trim()) return;
    setSaving(true); setSaveError(null);
    try {
      const payload = { ...draft, title: draft.title.trim() };
      const result = adding ? await createRisk(projectId, payload) : await updateRisk(projectId, editing, payload);
      onChange((prev) => adding ? [...prev, result] : prev.map((item) => item.id === result.id ? result : item));
      setEditing(null); setDraft(null);
    } catch (err) { setSaveError(err?.message || "Save failed."); log.warn("ProjectInlineExpanded: risk save failed", { err: String(err) }); }
    finally { setSaving(false); }
  };
  const sorted = useMemo(() => sortRisksBySeverity(risks), [risks]);
  return (
    <Section title={`Risks (${risks.length})`} action={canEdit && <ActionButton onClick={() => start(null)} testId="project-add-risk">+ Add</ActionButton>}>
      {sorted.length === 0 && !draft ? <Empty>No risks recorded.</Empty> : sorted.map((item) => (
        <div key={item.id} data-testid={`project-risk-${item.id}`} style={{ borderBottom: `1px solid ${T.border}`, padding: "7px 0" }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
            <strong style={{ fontSize: T.fsSm, color: T.dark }}>{item.title}</strong>
            <Pill color={item.severity === "high" ? T.red : item.severity === "med" ? T.amberDark : T.green} background={item.severity === "high" ? T.redSoft : T.lightGray}>{item.severity || "—"}</Pill>
          </div>
          <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 3 }}>{item.mitigation || "No mitigation"}{item.owner ? ` · ${item.owner}` : ""}</div>
          {item.description && <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 3 }}>{item.description}</div>}
          {canEdit && <button type="button" onClick={() => start(item)} style={{ border: 0, background: "none", color: T.blue, padding: "5px 0 0", fontSize: T.fsXs, cursor: "pointer" }}>Edit</button>}
          {editing === item.id && <ChildForm kind="risk" value={draft} onChange={(key, value) => setDraft((prev) => ({ ...prev, [key]: value }))} onSave={save} onCancel={() => { setEditing(null); setDraft(null); }} saving={saving} />}
        </div>
      ))}
      {adding && draft && <ChildForm kind="risk" value={draft} onChange={(key, value) => setDraft((prev) => ({ ...prev, [key]: value }))} onSave={save} onCancel={() => { setEditing(null); setDraft(null); }} saving={saving} />}
    {saveError && <div role="alert" data-testid="project-child-save-error" style={{ color: T.red, fontSize: T.fsXs, marginTop: 6 }}>{saveError}</div>}
    </Section>
  );
}

function MilestoneList({ projectId, projectTimeZone, milestones, canEdit, onChange }) {
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const start = (item) => {
    setSaveError(null);
    setEditing(item?.id ?? "new"); setAdding(!item);
    setDraft(item ? {
      name: item.name ?? "",
      // Keep the calendar day from the API, without converting through a timezone.
      dueDate: typeof item.dueDate === "string" ? item.dueDate.match(/^\d{4}-\d{2}-\d{2}(?=$|T)/)?.[0] ?? "" : "",
      status: item.status ?? "on_track",
      notes: item.notes ?? "",
    } : { name: "", dueDate: "", status: "on_track", notes: "" });
  };
  const save = async () => {
    if (!draft?.name?.trim()) return;
    setSaving(true); setSaveError(null);
    try {
      const result = adding ? await createMilestone(projectId, draft) : await updateMilestone(projectId, editing, draft);
      onChange((prev) => adding ? [...prev, result] : prev.map((item) => item.id === result.id ? result : item));
      setEditing(null); setDraft(null);
    } catch (err) { setSaveError(err?.message || "Save failed."); log.warn("ProjectInlineExpanded: milestone save failed", { err: String(err) }); }
    finally { setSaving(false); }
  };
  const form = draft && (
    <div data-testid="project-milestone-form" style={{ background: T.blueSoft, borderRadius: 8, padding: 10, marginTop: 8 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 150px), 1fr))", gap: 8 }}>
        <Field label="Name" value={draft.name} onChange={(v) => setDraft((p) => ({ ...p, name: v }))} />
        <Field label="Due date" type="date" value={draft.dueDate} onChange={(v) => setDraft((p) => ({ ...p, dueDate: v }))} />
        <SelectField label="Status" value={draft.status} onChange={(v) => setDraft((p) => ({ ...p, status: v }))} options={Object.entries(MILESTONE_STATUS_LABELS)} />
      </div>
      <Field label="Notes" value={draft.notes} onChange={(v) => setDraft((p) => ({ ...p, notes: v }))} />
      <div style={{ display: "flex", gap: 6 }}><ActionButton onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</ActionButton><ActionButton onClick={() => { setEditing(null); setDraft(null); }} secondary disabled={saving}>Cancel</ActionButton></div>
    </div>
  );
  const orderedMilestones = [...milestones].filter(m => !m.deletedAt)
    .sort((a, b) => String(a.dueDate || "9999-12-31").localeCompare(String(b.dueDate || "9999-12-31")));
  const nextMilestone = selectNextMilestone(orderedMilestones);
  const remainingMilestones = orderedMilestones.filter((item) => item.id !== nextMilestone?.id);
  return (
    <Section embedded title={`Milestones (Big Rocks) · ${milestones.length}`} action={canEdit && <ActionButton onClick={() => start(null)} testId="project-add-milestone">+ Add</ActionButton>}>
      {nextMilestone && (
        <div data-testid="project-next-milestone" style={{ background: T.blueSoft, borderRadius: 8, padding: "9px 10px", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
            <div style={{ color: T.blue, fontSize: T.fsXs, fontWeight: T.fwBold, textTransform: "uppercase", letterSpacing: ".04em" }}>Next milestone</div>
            {canEdit && <button type="button" data-testid="project-next-milestone-edit" onClick={() => start(nextMilestone)} style={{ border: 0, background: "none", color: T.blue, fontSize: T.fsXs, cursor: "pointer" }}>Edit</button>}
          </div>
          <strong style={{ display: "flex", alignItems: "center", gap: 8, color: T.dark, fontSize: T.fsSm, marginTop: 3 }}><MilestoneDot status={nextMilestone.status} />{nextMilestone.name}</strong>
          <MilestoneSignal milestone={nextMilestone} timeZone={projectTimeZone} />
          <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 2 }}>
            {nextMilestone.dueDate ? fmtDate(nextMilestone.dueDate) : "No due date"} · {MILESTONE_STATUS_LABELS[nextMilestone.status] || nextMilestone.status}
          </div>
          {editing === nextMilestone.id && form}
        </div>
      )}
      {milestones.length === 0 && !draft ? <Empty>No milestones recorded.</Empty> : remainingMilestones.map((item) => (
        <div key={item.id} data-testid={`project-milestone-${item.id}`} style={{ borderBottom: `1px solid ${T.border}`, padding: "7px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <MilestoneDot status={item.status} />
            <MilestoneSignal milestone={item} timeZone={projectTimeZone} />
            <strong style={{ flex: "1 1 120px", minWidth: 0, overflowWrap: "anywhere", fontSize: T.fsSm }}>{item.name}</strong>
            <span style={{ color: T.mid, fontSize: T.fsXs }}>{item.dueDate ? fmtDate(item.dueDate) : "No due date"} · {MILESTONE_STATUS_LABELS[item.status] || item.status}</span>
            {canEdit && <button type="button" onClick={() => start(item)} style={{ border: 0, background: "none", color: T.blue, fontSize: T.fsXs, cursor: "pointer" }}>Edit</button>}
          </div>
          {editing === item.id && form}
        </div>
      ))}
      {adding && form}
    {saveError && <div role="alert" data-testid="project-child-save-error" style={{ color: T.red, fontSize: T.fsXs, marginTop: 6 }}>{saveError}</div>}
    </Section>
  );
}

function MilestoneDot({ status }) {
  return <span aria-hidden="true" style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", flexShrink: 0, background: status === "complete" ? T.green : status === "at_risk" ? T.pendingAmber : T.borderDark }} />;
}

function WeeklyUpdate({ projectId, updates, canEdit, onChange }) {
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({ weekEnding: "", statusOverall: "on_track", narrative: "", accomplishments: "", blockers: "" });
  const [saving, setSaving] = useState(false);
  const latest = updates[0];
  const save = async () => {
    if (!draft.narrative.trim() && !draft.blockers.trim()) return;
    setSaving(true);
    try {
      const result = await createWeeklyUpdate(projectId, draft);
      onChange((prev) => [result, ...prev]);
      setAdding(false);
      setDraft({ weekEnding: "", statusOverall: "on_track", narrative: "", accomplishments: "", blockers: "" });
    } catch (err) { log.warn("ProjectInlineExpanded: weekly update save failed", { err: String(err) }); }
    finally { setSaving(false); }
  };
  return (
    <Section title="Latest weekly update" action={canEdit && <ActionButton onClick={() => setAdding((v) => !v)} testId="project-add-weekly-update">+ Add update</ActionButton>}>
      {latest ? (
        <div data-testid="project-latest-weekly-update">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}><StatusPill status={latest.statusOverall} />{latest.weekEnding && <span style={{ color: T.mid, fontSize: T.fsXs }}>Week ending {fmtDate(latest.weekEnding)}</span>}</div>
          {latest.narrative && <p style={{ margin: "0 0 7px", fontSize: T.fsSm, color: T.dark, lineHeight: 1.5 }}>{weeklyText(latest.narrative)}</p>}
          {latest.accomplishments && <div style={{ color: T.green, fontSize: T.fsXs, marginBottom: 4 }}><strong>Accomplishments:</strong> {weeklyText(latest.accomplishments)}</div>}
          {latest.blockers && <div style={{ color: T.red, fontSize: T.fsXs }}><strong>Blockers:</strong> {latest.blockers}</div>}
        </div>
      ) : <Empty>No weekly updates recorded.</Empty>}
      {adding && <div data-testid="project-weekly-update-form" style={{ background: T.blueSoft, borderRadius: 8, padding: 10, marginTop: 10 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}><Field label="Week ending" type="date" value={draft.weekEnding} onChange={(v) => setDraft((p) => ({ ...p, weekEnding: v }))} /><SelectField label="Status" value={draft.statusOverall} onChange={(v) => setDraft((p) => ({ ...p, statusOverall: v }))} options={Object.entries(PROJECT_STATUS).map(([key, value]) => [key, value.label])} /></div>
        <label style={{ display: "block", marginBottom: 8 }}><span style={labelStyle}>Narrative</span><textarea value={draft.narrative} onChange={(e) => setDraft((p) => ({ ...p, narrative: e.target.value }))} style={{ ...inputStyle, minHeight: 64, resize: "vertical" }} /></label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}><Field label="Accomplishments" value={draft.accomplishments} onChange={(v) => setDraft((p) => ({ ...p, accomplishments: v }))} /><Field label="Blockers" value={draft.blockers} onChange={(v) => setDraft((p) => ({ ...p, blockers: v }))} /></div>
        <div style={{ display: "flex", gap: 6 }}><ActionButton onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</ActionButton><ActionButton onClick={() => setAdding(false)} secondary disabled={saving}>Cancel</ActionButton></div>
      </div>}
    </Section>
  );
}

const INTEL_FIELD_LABELS = {
  target: "FDOB",
  milestone: "Next milestone",
  seats: "Seats",
  status: "Project status",
  note: "Project note",
};

const INTEL_CONFIDENCE = {
  high: { label: "High confidence", color: T.green, background: T.greenSoft },
  med: { label: "Medium confidence", color: T.amberDark, background: T.utilAmber },
  needsReview: { label: "Needs review", color: T.red, background: T.redSoft },
};

function proposalText(proposal) {
  const value = proposal.proposedValue;
  if (proposal.field === "milestone" && value && typeof value === "object") {
    return `${value.name || "Milestone"}${value.dueDate ? ` · ${fmtDate(value.dueDate)}` : ""}`;
  }
  if (proposal.field === "seats" && value && typeof value === "object") return `+${value.delta} seats`;
  if (proposal.field === "status") return String(value).replaceAll("_", " ");
  if (proposal.field === "target") return fmtDate(value) || value;
  return value == null ? "—" : String(value);
}

function proposalEditValue(proposal) {
  if (proposal.field === "milestone") return JSON.stringify(proposal.proposedValue, null, 2);
  if (proposal.field === "seats" && proposal.proposedValue && typeof proposal.proposedValue === "object") {
    return String(proposal.proposedValue.delta ?? "");
  }
  return String(proposal.proposedValue ?? "");
}

function parseProposalEdit(proposal, value) {
  if (proposal.field === "seats") return Number(value);
  if (proposal.field === "milestone") {
    try { return JSON.parse(value); } catch { throw new Error("Milestone must be valid JSON."); }
  }
  return value;
}

function readTextSnapshot(file) {
  return new Promise((resolve, reject) => {
    if (!file || file.size > 50_000) {
      reject(new Error("Text snapshots must be 50,000 characters or fewer."));
      return;
    }
    if (typeof file.text === "function") {
      file.text().then(resolve).catch(reject);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read the text snapshot."));
    reader.readAsText(file);
  });
}

function SourceSnapshotForm({ onExtract, busy }) {
  const [sources, setSources] = useState(() => [{
    id: "source-1",
    source_type: "gdoc",
    source_label: "",
    content_text: "",
  }]);
  const [formError, setFormError] = useState(null);

  const updateSource = (id, key, value) => {
    setSources((items) => items.map((item) => item.id === id ? { ...item, [key]: value } : item));
  };

  const loadFile = async (id, event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      updateSource(id, "content_text", await readTextSnapshot(file));
      setFormError(null);
    } catch (error) {
      setFormError(error);
    } finally {
      event.target.value = "";
    }
  };

  const submit = async () => {
    const payload = sources.map(({ id: _id, ...source }) => ({
      ...source,
      source_label: source.source_label.trim(),
      content_text: source.content_text,
    }));
    const invalid = payload.find((source) => !source.source_label || !source.content_text.trim());
    if (invalid) {
      setFormError(new Error("Each source needs a label and text snapshot."));
      return;
    }
    setFormError(null);
    await onExtract(payload);
  };

  return (
    <div data-testid="projects-intel-extraction-form" style={{ marginTop: 12, padding: 12, background: T.blueSoft, borderRadius: 8 }}>
      <div style={{ color: T.dark, fontWeight: T.fwSemibold, fontSize: T.fsSm }}>Pull key points from sources</div>
      <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 3 }}>Paste a curated document or Slack export. Snapshots are sent for this run only and are not saved.</div>
      {formError && <div data-testid="projects-intel-extraction-form-error" style={{ color: T.red, fontSize: T.fsXs, marginTop: 8 }}>{formError.message}</div>}
      {sources.map((source, index) => (
        <div key={source.id} data-testid={`projects-intel-source-${index}`} style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr auto", gap: 8, alignItems: "end" }}>
            <SelectField
              label="Source type"
              value={source.source_type}
              onChange={(value) => updateSource(source.id, "source_type", value)}
              options={[["gdoc", "Document"], ["slack", "Slack"]]}
            />
            <Field
              label="Source label"
              value={source.source_label}
              onChange={(value) => updateSource(source.id, "source_label", value)}
              placeholder={source.source_type === "slack" ? "#project-channel" : "Weekly project snapshot"}
            />
            {sources.length > 1 && <button type="button" onClick={() => setSources((items) => items.filter((item) => item.id !== source.id))} style={{ border: 0, background: "none", color: T.red, fontSize: T.fsXs, padding: "6px 0", cursor: "pointer" }}>Remove</button>}
          </div>
          <label style={{ display: "block", marginBottom: 8 }}>
            <span style={labelStyle}>Text snapshot</span>
            <textarea
              data-testid={`projects-intel-source-text-${index}`}
              value={source.content_text}
              onChange={(event) => updateSource(source.id, "content_text", event.target.value)}
              placeholder="Paste source text here…"
              style={{ ...inputStyle, minHeight: 82, resize: "vertical" }}
            />
          </label>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, color: T.blue, fontSize: T.fsXs, cursor: "pointer" }}>
            Load a text snapshot
            <input
              data-testid={`projects-intel-source-file-${index}`}
              type="file"
              accept=".txt,text/plain"
              onChange={(event) => loadFile(source.id, event)}
              style={{ maxWidth: 190 }}
            />
          </label>
        </div>
      ))}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <ActionButton
          secondary
          onClick={() => setSources((items) => [...items, { id: `source-${items.length + 1}-${Date.now()}`, source_type: "slack", source_label: "", content_text: "" }])}
          disabled={busy || sources.length >= 8}
          testId="projects-intel-add-source"
        >
          + Add source
        </ActionButton>
        <ActionButton onClick={submit} disabled={busy} testId="projects-intel-extract-button">
          {busy ? "Extracting…" : "Pull key points from sources"}
        </ActionButton>
      </div>
    </div>
  );
}

export function ProjectsIntelReviewPanel({ projectId, currentRole, onProjectUpdated, onMilestoneChanged }) {
  const flags = useFeatureFlags();
  const canReview = WRITE_ROLES.has(currentRole) && flags.projects_intel === true;
  const canExtract = currentRole === "admin" && flags.projects_intel === true;
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState(null);
  const [extractionBusy, setExtractionBusy] = useState(false);

  useEffect(() => {
    if (!canReview) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getProjectIntelProposals(projectId)
      .then((items) => { if (!cancelled) setProposals(items); })
      .catch((err) => { if (!cancelled) setError(err); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [canReview, projectId]);

  if (!canReview) return null;

  const runExtraction = async (sources) => {
    setExtractionBusy(true);
    setError(null);
    setMessage(null);
    try {
      const result = await extractProjectIntel(projectId, sources);
      const refreshed = await getProjectIntelProposals(projectId);
      setProposals(refreshed);
      setMessage(`Extraction complete: ${result.acceptedCount || 0} accepted, ${result.skippedCount || 0} skipped, ${result.rejectedCount || 0} rejected.`);
    } catch (err) {
      setError(err);
    } finally {
      setExtractionBusy(false);
    }
  };

  const saveEdit = async (proposal) => {
    setBusyId(proposal.id);
    setError(null);
    try {
      const updated = await editProjectIntelProposal(projectId, proposal.id, parseProposalEdit(proposal, editValue));
      setProposals((items) => items.map((item) => item.id === updated.id ? updated : item));
      setEditingId(null);
      setMessage("Draft updated. It is still waiting for approval.");
    } catch (err) {
      setError(err);
    } finally {
      setBusyId(null);
    }
  };

  const transition = async (proposal, action) => {
    setBusyId(proposal.id);
    setError(null);
    setMessage(null);
    try {
      if (action === "approve") {
        const result = await approveProjectIntelProposal(projectId, proposal.id);
        setProposals((items) => items.filter((item) => item.id !== proposal.id));
        if (result.project) onProjectUpdated?.({ ...result.project, __intelCanonicalPatch: true });
        if (result.milestone) onMilestoneChanged?.((items) => {
          const found = items.some((item) => item.id === result.milestone.id);
          return found ? items.map((item) => item.id === result.milestone.id ? result.milestone : item) : [...items, result.milestone];
        });
        setMessage("Approved and published to the canonical project data.");
      } else {
        await dismissProjectIntelProposal(projectId, proposal.id);
        setProposals((items) => items.filter((item) => item.id !== proposal.id));
        setMessage("Dismissed. Leadership data was not changed.");
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <section data-testid="projects-intel-review-panel" style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline", borderBottom: `1px solid ${T.border}`, paddingBottom: 10 }}>
        <div>
          <h3 style={{ margin: 0, color: T.dark, fontSize: T.fsMd }}>Proposed updates</h3>
          <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 2 }}>Review the evidence before anything reaches the leadership view.</div>
        </div>
        <Pill color={T.green} background={T.greenSoft}>{proposals.length} draft{proposals.length === 1 ? "" : "s"}</Pill>
      </div>
      {error && <div style={{ marginTop: 10 }}><ErrorBanner error={error} onDismiss={() => setError(null)} /></div>}
      {message && <div data-testid="projects-intel-feedback" style={{ color: T.green, background: T.greenSoft, borderRadius: 6, padding: "7px 9px", marginTop: 10, fontSize: T.fsXs }}>{message}</div>}
      {canExtract && <SourceSnapshotForm onExtract={runExtraction} busy={extractionBusy} />}
      {loading ? <div style={{ padding: "14px 0", color: T.mid, fontSize: T.fsSm }}>Loading proposed updates…</div>
        : proposals.length === 0 ? <Empty>No draft updates are waiting for review.</Empty>
          : proposals.map((proposal) => {
            const confidence = INTEL_CONFIDENCE[proposal.confidence === "needs_review" ? "needsReview" : proposal.confidence] || INTEL_CONFIDENCE.needsReview;
            const busy = busyId === proposal.id;
            return (
              <article key={proposal.id} data-testid={`projects-intel-proposal-${proposal.id}`} style={{ border: `1px solid ${T.border}`, borderRadius: 8, padding: "11px 12px", marginTop: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center" }}>
                  <Pill color={proposal.field === "status" ? T.red : T.blue} background={proposal.field === "status" ? T.redSoft : T.blueSoft}>{INTEL_FIELD_LABELS[proposal.field] || proposal.field}</Pill>
                  <Pill color={confidence.color} background={confidence.background}>{confidence.label}</Pill>
                </div>
                {editingId === proposal.id ? (
                  <textarea data-testid={`projects-intel-edit-${proposal.id}`} value={editValue} onChange={(event) => setEditValue(event.target.value)} style={{ ...inputStyle, marginTop: 9, minHeight: proposal.field === "milestone" ? 82 : 38, resize: "vertical" }} />
                ) : (
                  <div data-testid={`projects-intel-value-${proposal.id}`} style={{ color: T.dark, fontWeight: T.fwSemibold, fontSize: T.fsSm, marginTop: 9 }}>{proposalText(proposal)}</div>
                )}
                {proposal.rationale && <div style={{ color: T.mid, fontSize: T.fsXs, marginTop: 5 }}>{proposal.rationale}</div>}
                {Array.isArray(proposal.provenance) && proposal.provenance.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 8 }}>
                    {proposal.provenance.map((source, index) => (
                      isSafeDocumentUrl(source.url)
                        ? <a key={`${source.sourceLabel}-${index}`} href={source.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}><Pill color={source.sourceType === "slack" ? T.purpleDeep : T.blue} background={T.lightGray}>{source.sourceType === "slack" ? "# " : "D "}{source.sourceLabel}{source.locator ? ` · ${source.locator}` : ""}</Pill></a>
                        : <Pill key={`${source.sourceLabel}-${index}`} color={source.sourceType === "slack" ? T.purpleDeep : T.blue} background={T.lightGray}>{source.sourceType === "slack" ? "# " : "D "}{source.sourceLabel}{source.locator ? ` · ${source.locator}` : ""}</Pill>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 7, marginTop: 10 }}>
                  {editingId === proposal.id ? (
                    <>
                      <ActionButton onClick={() => saveEdit(proposal)} disabled={busy} testId={`projects-intel-save-${proposal.id}`}>{busy ? "Saving…" : "Save draft"}</ActionButton>
                      <ActionButton secondary onClick={() => setEditingId(null)} disabled={busy}>Cancel</ActionButton>
                    </>
                  ) : (
                    <>
                      <ActionButton onClick={() => transition(proposal, "approve")} disabled={busy} testId={`projects-intel-approve-${proposal.id}`}>{busy ? "Working…" : "Approve"}</ActionButton>
                      <ActionButton secondary onClick={() => { setEditingId(proposal.id); setEditValue(proposalEditValue(proposal)); }} disabled={busy} testId={`projects-intel-edit-button-${proposal.id}`}>Edit</ActionButton>
                      <button type="button" onClick={() => transition(proposal, "dismiss")} disabled={busy} data-testid={`projects-intel-dismiss-${proposal.id}`} style={{ border: `1px solid ${T.redSoft}`, borderRadius: 6, background: T.white, color: T.red, padding: "5px 9px", fontSize: T.fsXs, fontWeight: T.fwSemibold, cursor: busy ? "wait" : "pointer" }}>Dismiss</button>
                    </>
                  )}
                </div>
              </article>
            );
          })}
    </section>
  );
}

function Overview({ project, canEdit, onProjectUpdated, canSeeBudget, children }) {
  const variance = recordVariance(project);
  const officeLabel = [project.locationName, project.locationAlias, project.locationId].filter(Boolean).join(" · ");
  const officeValue = project.locationId ? (
    <a
      href={`?actionNav=location&actionNavId=${encodeURIComponent(project.locationId)}`}
      style={{ color: T.blue, textDecoration: "none" }}
    >
      {officeLabel || project.locationId}
    </a>
  ) : "—";
  const rows = [
    ["Office", officeValue],
    ["Type", PROJECT_TYPE[project.projectType]?.label || project.projectType || "—"],
    ["Phase", getProjectPhaseDisplayLabel(project.phase)],
    ["Status", <StatusPill status={project.statusOverall} />],
    ["Internal owner", project.internalOwner || "—"],
    ["JLL owner", project.jllOwner || "—"],
    ["Start date", fmtDate(project.startDate) || "—"],
    ["FDOB", project.fdobDate ? fmtDate(project.fdobDate) : "Not recorded"],
    ["Seats coming", project.plannedSeats == null ? "—" : `${Number(project.plannedSeats).toLocaleString()} seats`],
  ];
  return (
    <div data-testid="project-overview" style={{ minWidth: 0 }}>
    <Section record title="THE RECORD · PMO" action={canEdit && <ActionButton testId="project-inline-edit-button" onClick={() => onProjectUpdated({ __edit: true })}>Edit record</ActionButton>}>
      <div style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwBold }}>Project info</div>
      <div className="project-overview-fields">
        {rows.map(([label, value]) => <div key={label} style={{ minWidth: 0, padding: "5px 0", fontSize: T.fsSm }}><div style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwSemibold }}>{label}</div><strong style={{ color: T.dark, overflowWrap: "anywhere" }}>{value}</strong></div>)}
      </div>
      {canSeeBudget && (
        <div data-testid="project-financials" style={{ borderTop: `1px solid ${T.border}`, marginTop: 14, paddingTop: 12 }}>
          <div style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwBold, marginBottom: 8 }}>Financials</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "12px 18px" }}>
            {[
              ["approved-budget", "Approved budget", project.approvedBudget],
              ["forecast", "Forecast at completion", project.forecastAtCompletion],
              ["spent", "Spent to date", project.spentToDate],
              ["variance", "Variance", variance],
            ].map(([key, label, value]) => (
              <div key={key} data-testid={`project-${key}`} style={{ minWidth: 0 }}>
                <div style={{ color: T.mid, fontSize: T.fsXs }}>{label}</div>
                <strong style={{ color: key === "variance" ? budgetHealthColor(budgetHealth(project.financialsTracked === false ? null : project.approvedBudget, project.forecastAtCompletion)) : T.dark, fontSize: T.fsMd, overflowWrap: "anywhere" }}>
                  {formatRecordAmount(value, project.currency, key === "variance")}
                </strong>
                {key === "variance" && <BudgetHealthLabel budget={project.financialsTracked === false ? null : project.approvedBudget} forecast={project.forecastAtCompletion} currency={project.currency} />}
              </div>
            ))}
          </div>
        </div>
      )}
      <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 12, paddingTop: 10 }}>
        <div style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwSemibold, marginBottom: 4 }}>Summary</div>
        <div style={{ color: T.dark, fontSize: T.fsSm, whiteSpace: "pre-wrap" }}>{project.oneLineSummary || "—"}</div>
      </div>
      {project.notes && (
        <div data-testid="project-internal-notes" style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${T.border}` }}>
          <div style={{ color: T.mid, fontSize: T.fsXs, fontWeight: T.fwSemibold, marginBottom: 4 }}>Internal notes</div>
          <div style={{ color: T.dark, fontSize: T.fsSm, whiteSpace: "pre-wrap" }}>{project.notes}</div>
        </div>
      )}
      {children}
    </Section>
    </div>
  );
}

export function ProjectInlineExpanded({ projectId, rowData, currentRole = "viewer", onProjectUpdated, onArchived, onCollapse, sourceData = null, portfolioSources = [], onSourcesChange, onOpenMyUpdate, weeklyRefresh = 0, weeklyArchiveCache, onOpenWeeklyArchive }) {
  const [project, setProject] = useState(rowData || null);
  const [risks, setRisks] = useState([]);
  const [milestones, setMilestones] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [slackSource, setSlackSource] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingProject, setEditingProject] = useState(false);
  const canEdit = WRITE_ROLES.has(currentRole);
  const sensitivity = useSensitivityPolicy();
  const canSeeBudget = sensitivity.canSee("projects.budget");

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError(null);
    setSlackSource(null);
    Promise.resolve()
      .then(() => getProjectSlackSource(projectId))
      .then((slack) => {
        if (!cancelled) setSlackSource(slack);
      })
      .catch(() => {
        // Slack status is supplemental and must never block project details.
        if (!cancelled) {
          setSlackSource({
            provider: "slack",
            type: "channel",
            reference: rowData?.slackChannel || null,
            configured: Boolean(rowData?.slackChannel),
            readable: false,
            status: rowData?.slackChannel ? "degraded" : "unconfigured",
            reason: rowData?.slackChannel ? "request_failed" : "missing_reference",
            messages: [],
            messageCount: 0,
          });
        }
      });
    Promise.all([
      Promise.resolve().then(() => getProject(projectId)),
      Promise.resolve().then(() => getProjectRisks(projectId)).catch(() => []),
      Promise.resolve().then(() => getProjectMilestones(projectId)).catch(() => []),
      Promise.resolve().then(() => getProjectDocuments(projectId)).catch(() => []),
     ]).then(([p, r, m, docs]) => {
      if (cancelled) return;
      setProject(p || rowData || null);
       setRisks(Array.isArray(r) ? r : []);
      setMilestones(Array.isArray(m) ? m : []);
      setDocuments(Array.isArray(docs) ? docs : []);
      setLoading(false);
    }).catch((err) => {
      if (cancelled) return;
      log.warn("ProjectInlineExpanded: load failed", { projectId, err: String(err) });
      setError(err);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [projectId, rowData]);

  const notifyProject = (updated) => {
    const { __intelCanonicalPatch, ...changes } = updated || {};
    const next = __intelCanonicalPatch ? { ...project, ...changes } : updated;
    setProject(next);
    if (typeof onProjectUpdated === "function") onProjectUpdated(next);
  };

  const refreshMilestonesAfterProjectEdit = async (updated) => {
    notifyProject(updated);
    try {
      const refreshedMilestones = await getProjectMilestones(projectId);
      setMilestones(Array.isArray(refreshedMilestones) ? refreshedMilestones : []);
      notifyProject({ ...updated, nextMilestone: selectNextPortfolioMilestone(refreshedMilestones), milestonesError: false });
    } catch (err) {
      log.warn("ProjectInlineExpanded: milestone refresh after edit failed", { projectId, err: String(err) });
    }
  };

  const refreshProjectAfterMilestoneEdit = async (change) => {
    setMilestones(change);
    try {
      const [updated, refreshedMilestones] = await Promise.all([
        getProject(projectId),
        getProjectMilestones(projectId),
      ]);
      if (updated) notifyProject({ ...updated, nextMilestone: selectNextPortfolioMilestone(refreshedMilestones), milestonesError: false });
      setMilestones(Array.isArray(refreshedMilestones) ? refreshedMilestones : []);
    } catch (err) {
      log.warn("ProjectInlineExpanded: project refresh after milestone edit failed", { projectId, err: String(err) });
    }
  };

  if (loading && !project) return <div data-testid={`project-inline-loading-${projectId}`} style={{ padding: 24, color: T.mid }}>Loading project details…</div>;
  if (error && !project) return <div style={{ padding: 18 }}><ErrorBanner error={error} /></div>;
  if (!project) return <div style={{ padding: 18, color: T.mid }}>Project not found.</div>;
  return (
    <>
      <div data-testid={`project-inline-expanded-${projectId}`} className="project-inline-detail" style={{ background: T.tint, borderTop: `1px solid ${T.border}`, padding: 16, containerType: "inline-size" }}>
      <style>{`
        .project-inline-header { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
        .project-inline-actions { display: flex; align-items: center; gap: 8px; margin-left: auto; flex-wrap: wrap; }
        .project-inline-columns { display: grid; grid-template-columns: minmax(0, 1.25fr) minmax(0, 1fr); gap: 18px; align-items: start; }
        .project-inline-columns > * { min-width: 0; overflow-wrap: anywhere; }
        .project-overview-fields { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px 20px; }
        @container (max-width: 820px) {
          .project-inline-columns { grid-template-columns: minmax(0, 1fr); }
        }
        @container (max-width: 420px) {
          .project-overview-fields { grid-template-columns: minmax(0, 1fr); }
        }
      `}</style>
      <div className="project-inline-header">
        <div style={{ minWidth: 0 }}><div style={{ display: "flex", alignItems: "center", gap: 8 }}><ProjectHealthRing health={projectStatusToHealthKey(project.statusOverall)} /><strong style={{ color: T.projectsHubNavy, fontSize: T.fsMd, overflowWrap: "anywhere" }}>{project.name}</strong></div><div data-testid="project-identity" style={{ color: T.mid, fontSize: T.fsXs, marginTop: 4, overflowWrap: "anywhere" }}>{recordIdentity(project, PROJECT_TYPE[project.projectType]?.label || project.projectType)}</div></div>
        <div className="project-inline-actions"><Connections projectId={projectId} project={project} documents={documents} slackSource={slackSource} canEdit={canEdit} onChange={setDocuments} onProjectUpdated={notifyProject} sourceData={sourceData} onSourcesChange={onSourcesChange} /><ActionButton secondary onClick={onCollapse} testId={`project-collapse-${projectId}`}>Collapse</ActionButton></div>
      </div>
      <ProjectPhaseTracker phase={project.phase} />
      <ProjectsIntelReviewPanel
        projectId={projectId}
        currentRole={currentRole}
        onProjectUpdated={notifyProject}
        onMilestoneChanged={(change) => { void refreshProjectAfterMilestoneEdit(change); }}
      />
      <div data-testid="project-ownership-legend" style={{ display: "grid", gap: 6, background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 16px", marginBottom: 12, color: T.mid, fontSize: T.fsXs }}>
        {[
          [T.projectsHubNavy, "The record - what's true now. PMO-owned, changes deliberately."],
          [T.projectsHubLive, "Working updates - what changed this week. Many contributors, feeds the Weekly Review."],
        ].map(([color, text]) => <div key={text} style={{ display: "flex", alignItems: "baseline", gap: 8 }}><span aria-hidden="true" style={{ width: 8, height: 8, flexShrink: 0, background: color }} /><span>{text}</span></div>)}
      </div>
      <div className="project-inline-columns" data-testid="project-detail-columns">
         <Overview project={project} canEdit={canEdit} canSeeBudget={canSeeBudget} onProjectUpdated={(value) => value.__edit && setEditingProject(true)}>
        <div data-testid="project-milestones" style={{ minWidth: 0 }}><MilestoneList projectId={projectId} projectTimeZone={project.projectTimeZone} milestones={milestones} canEdit={canEdit} onChange={(change) => { void refreshProjectAfterMilestoneEdit(change); }} /></div>
         </Overview>
         <ProjectWorkingUpdates projectId={projectId} canPost={canEdit && Boolean(onOpenMyUpdate)} onPost={onOpenMyUpdate} refreshToken={weeklyRefresh} archiveCache={weeklyArchiveCache} onOpenArchive={onOpenWeeklyArchive} />
      </div>
      </div>
      <ProjectEditModal
        isOpen={editingProject}
        mode="edit"
        project={project}
        canArchive={currentRole === "admin" && canEdit}
        onArchived={(id) => { setEditingProject(false); onArchived?.(id); onCollapse?.(); }}
        onClose={() => setEditingProject(false)}
        onSaved={(updated) => { void refreshMilestonesAfterProjectEdit(updated); setEditingProject(false); }}
      />
    </>
  );
}